import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Container,
  Grid,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  ButtonGroup,
  Chip,
  Snackbar,
  TextField,
  withStyles
} from '@material-ui/core';
import {
  Assessment as AssessmentIcon
} from '@material-ui/icons';

// Import all required components
import QuickNotesYtdChart from './QuickNotesYtdChart';
import QuickNotesOwnerChart from './QuickNotesOwnerChart';
import QuickNotesDrilldownModal from './QuickNotesDrilldownModal';
import QuickNotesExportButton from './QuickNotesExportButton';
import ErrorBoundary from '../ErrorBoundary';

// Import GraphQL queries and helpers
import {
  GET_CURRENT_USER,
  GET_QUICK_NOTE_CATEGORIES,
  DEFAULT_QUERY_OPTIONS
} from './QuickNotesReportsQuery';

// Custom Alert component for compatibility with older Material-UI
const Alert = ({ severity, children, style }) => {
  const severityColors = {
    error: '#f44336',
    warning: '#ff9800', 
    info: '#2196f3',
    success: '#4caf50'
  };
  
  const severityBackgrounds = {
    error: '#ffebee',
    warning: '#fff3e0',
    info: '#e3f2fd', 
    success: '#e8f5e9'
  };

  return (
    <Paper
      style={{
        backgroundColor: severityBackgrounds[severity] || severityBackgrounds.info,
        borderLeft: `4px solid ${severityColors[severity] || severityColors.info}`,
        padding: '12px 16px',
        ...style
      }}
    >
      <Typography variant="body2" style={{ color: severityColors[severity] || severityColors.info }}>
        {children}
      </Typography>
    </Paper>
  );
};

/**
 * QuickNotesReportsContainer - Main container component for Quick Notes reporting
 * 
 * This container manages:
 * - Overall application state (tabs, filters, drill-down modal)
 * - Data coordination between chart components
 * - Global error handling and loading states
 * - User context and permissions
 * - Export functionality coordination
 * 
 * State Management Architecture:
 * - Local state for UI (tabs, modals, filters)
 * - GraphQL Apollo cache for data (queries handled by individual charts)
 * - Shared context passed down to child components
 * - Event handlers for cross-component communication
 */

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    minHeight: '100vh'
  },
  header: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3)
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main
    }
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1)
  },
  tabPanel: {
    padding: theme.spacing(3)
  },
  controlsRow: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  metricsControls: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  actionControls: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  monthSelector: {
    minWidth: 200,
    '& .MuiFormHelperText-root': {
      margin: theme.spacing(0.5, 0, 0, 0)
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  errorContainer: {
    margin: theme.spacing(2, 0)
  },
  chartContainer: {
    marginBottom: theme.spacing(3)
  }
});

// Tab constants
const TABS = {
  YTD: 0,
  OWNER: 1
};

const TAB_LABELS = {
  [TABS.YTD]: 'By Type',
  [TABS.OWNER]: 'By Owner'
};

// Metric constants
const METRICS = {
  COUNT: 'count',
  TIME_SPENT: 'time_spent'
};

const METRIC_LABELS = {
  [METRICS.COUNT]: 'Count',
  [METRICS.TIME_SPENT]: 'Time Spent'
};

// Stack dimension constants
const STACK_DIMENSIONS = {
  ACTIVITY_TYPE: 'ACTIVITY_TYPE',
  PLANNED: 'PLANNED',
  CATEGORY: 'CATEGORY'
};

const STACK_LABELS = {
  [STACK_DIMENSIONS.ACTIVITY_TYPE]: 'Activity Type',
  [STACK_DIMENSIONS.PLANNED]: 'Planned vs Unplanned',
  [STACK_DIMENSIONS.CATEGORY]: 'Category'
};

// Period constants
const PERIODS = {
  THIS_YEAR: 'this_year',
  LAST_SIX_MONTHS: 'last_six_months', 
  THIS_QUARTER: 'this_quarter',
  THIS_MONTH: 'this_month',
  CUSTOM: 'custom'
};

const PERIOD_LABELS = {
  [PERIODS.THIS_YEAR]: 'This Year',
  [PERIODS.LAST_SIX_MONTHS]: 'Last Six Months',
  [PERIODS.THIS_QUARTER]: 'This Quarter', 
  [PERIODS.THIS_MONTH]: 'This Month',
  [PERIODS.CUSTOM]: 'Custom Range'
};

const QuickNotesReports = ({ classes }) => {
  // ===== Local Snackbar state for user notifications =====
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const enqueueSnackbar = useCallback((message, options = {}) => {
    const severity = options.variant || 'info';
    setSnackbar({
      open: true,
      message: message,
      severity: severity
    });
  }, []);
  
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================

  // Tab and view state
  const [activeTab, setActiveTab] = useState(TABS.YTD);
  const [metric, setMetric] = useState(METRICS.COUNT);
  const [stackDimension, setStackDimension] = useState(STACK_DIMENSIONS.ACTIVITY_TYPE);

  // Period selector for both tabs
  const [selectedPeriod, setSelectedPeriod] = useState(PERIODS.THIS_YEAR);
  
  // Custom date range state
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null
  });

  // Month selector for Owner and Category views (deprecated - now using period filter)
  // Keeping for backward compatibility with other components that might still use it
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Month validation and auto-correction state
  const [monthValidationError, setMonthValidationError] = useState(null);

  // Drill-down modal state
  const [drilldownModal, setDrilldownModal] = useState({
    open: false,
    data: null
  });

  // Global loading and error state
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  // Refresh trigger state
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Advanced error handling and retry state
  const [retryState, setRetryState] = useState({
    isRetrying: false,
    retryCount: 0,
    maxRetries: 3,
    retryDelay: 1000, // Start with 1 second
    lastErrorTime: null,
    consecutiveErrors: 0
  });

  // Network status monitoring
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkRecovery, setNetworkRecovery] = useState(false);

  // =============================================================================
  // PERIOD CALCULATION UTILITIES
  // =============================================================================

  /**
   * Calculate date range based on selected period
   */
  const calculateDateRange = useCallback((period, customRange = null) => {
    const now = new Date();
    let startDate, endDate;

    switch (period) {
      case PERIODS.THIS_YEAR:
        startDate = new Date(now.getFullYear(), 0, 1); // January 1st
        endDate = new Date(now.getFullYear(), 11, 31); // December 31st
        break;
      
      case PERIODS.LAST_SIX_MONTHS:
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      
      case PERIODS.THIS_QUARTER:
        const currentQuarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
        endDate = new Date(now.getFullYear(), (currentQuarter + 1) * 3, 0); // Last day of quarter
        break;
      
      case PERIODS.THIS_MONTH:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of month
        break;
      
      case PERIODS.CUSTOM:
        if (customRange && customRange.startDate && customRange.endDate) {
          startDate = new Date(customRange.startDate);
          endDate = new Date(customRange.endDate);
        } else {
          // Default to this year if custom range not set
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date(now.getFullYear(), 11, 31);
        }
        break;
      
      default:
        // Default to this year
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
    }

    return {
      startDate: startDate.toISOString().split('T')[0], // YYYY-MM-DD format
      endDate: endDate.toISOString().split('T')[0]
    };
  }, []);

  /**
   * Get current active date range
   */
  const getActiveDateRange = useCallback(() => {
    return calculateDateRange(selectedPeriod, customDateRange);
  }, [selectedPeriod, customDateRange, calculateDateRange]);

  // =============================================================================
  // GRAPHQL QUERIES - User Context and Categories
  // =============================================================================

  // Get current user for context and permissions
  const { 
    data: userData, 
    loading: userLoading, 
    error: userError 
  } = useQuery(GET_CURRENT_USER, {
    ...DEFAULT_QUERY_OPTIONS,
    onError: (error) => {
      console.error('Failed to load user data:', error);
      enqueueSnackbar('Failed to load user information', { variant: 'error' });
    }
  });

  // Get categories for metadata
  const { 
    data: categoriesData, 
    loading: categoriesLoading, 
    error: categoriesError 
  } = useQuery(GET_QUICK_NOTE_CATEGORIES, {
    ...DEFAULT_QUERY_OPTIONS,
    onError: (error) => {
      console.error('Failed to load categories:', error);
      enqueueSnackbar('Failed to load Quick Note categories', { variant: 'error' });
    }
  });

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const currentUser = userData?.getCurrentUser;
  const categories = categoriesData?.getQuickNoteCategories || [];
  const userCompany = currentUser?.company;

  // Generate month options for selector (last 12 months + intelligent future months)
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Add last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      
      options.push({ 
        value, 
        label,
        isCurrentMonth: i === 0,
        isPreviousMonth: i === 1,
        isThisYear: date.getFullYear() === currentYear,
        sortOrder: i // For debugging/display ordering
      });
    }
    
    // Add metadata for validation
    options.currentMonthValue = options[0]?.value;
    options.oldestMonthValue = options[options.length - 1]?.value;
    
    return options;
  }, []);

  // Get current month label for display
  const getCurrentMonthLabel = useCallback(() => {
    const currentOption = monthOptions.find(opt => opt.isCurrentMonth);
    return currentOption?.label || 'Current Month';
  }, [monthOptions]);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  /**
   * Handle tab changes and reset relevant state
   */
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setDrilldownModal({ open: false, data: null }); // Close any open modal
    setGlobalError(null); // Clear any errors
    
    // Analytics tracking
    const tabName = TAB_LABELS[newValue];
  }, []);

  /**
   * Handle metric changes (Count vs Time Spent)
   */
  const handleMetricChange = useCallback((event) => {
    const newMetric = event.target.value;
    setMetric(newMetric);
    setDrilldownModal({ open: false, data: null }); // Close modal on metric change
  }, []);

  /**
   * Handle stack dimension changes (Activity Type, Planned, Category)
   */
  const handleStackDimensionChange = useCallback((event) => {
    const newStack = event.target.value;
    setStackDimension(newStack);
    setDrilldownModal({ open: false, data: null }); // Close modal on stack change
  }, []);

  /**
   * Handle period selection changes
   */
  const handlePeriodChange = useCallback((event) => {
    const newPeriod = event.target.value;
    setSelectedPeriod(newPeriod);
    setDrilldownModal({ open: false, data: null }); // Close modal on period change
    
    // Clear custom date range if switching away from custom
    if (newPeriod !== PERIODS.CUSTOM) {
      setCustomDateRange({ startDate: null, endDate: null });
    }
  }, []);

  /**
   * Handle custom date range changes
   */
  const handleCustomDateRangeChange = useCallback((field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  /**
   * Handle month selection for Owner and Category views
   */
  const handleMonthChange = useCallback((event) => {
    const newMonth = event.target.value;
    
    // Validate month format and availability
    if (!validateMonthSelection(newMonth)) {
      setMonthValidationError(`Invalid month selection: ${newMonth}`);
      enqueueSnackbar('Invalid month selected. Please choose a valid month.', { variant: 'warning' });
      return;
    }
    
    // Clear any validation errors
    setMonthValidationError(null);
    
    // Update state
    setSelectedMonth(newMonth);
    setDrilldownModal({ open: false, data: null }); // Close modal on month change
    
    // Get month info for analytics
    const monthOption = monthOptions.find(opt => opt.value === newMonth);
    const monthLabel = monthOption?.label || newMonth;
    const isCurrentMonth = monthOption?.isCurrentMonth || false;
    
    // Show user feedback for current month selection
    if (isCurrentMonth) {
      enqueueSnackbar(`Showing data for current month: ${monthLabel}`, { variant: 'info' });
    }
  }, [monthOptions, activeTab, enqueueSnackbar]);

  /**
   * Validate month selection
   */
  const validateMonthSelection = useCallback((month) => {
    if (!month) return false;
    
    // Check format YYYY-MM
    const monthRegex = /^\d{4}-\d{2}$/;
    if (!monthRegex.test(month)) return false;
    
    // Check if month is in available options
    const isAvailable = monthOptions.some(opt => opt.value === month);
    if (!isAvailable) return false;
    
    // Check if month is not in the future (beyond current month)
    const [year, monthNum] = month.split('-').map(Number);
    const monthDate = new Date(year, monthNum - 1, 1);
    const now = new Date();
    const currentMonthDate = new Date(now.getFullYear(), now.getMonth(), 1);
    
    if (monthDate > currentMonthDate) {
      return false;
    }
    
    return true;
  }, [monthOptions]);

  /**
   * Smart month selection helpers
   */
  const handleQuickMonthSelection = useCallback((action) => {
    let targetMonth;
    
    switch (action) {
      case 'current':
        targetMonth = monthOptions.currentMonthValue;
        break;
      case 'previous':
        const currentIndex = monthOptions.findIndex(opt => opt.value === selectedMonth);
        targetMonth = monthOptions[Math.min(currentIndex + 1, monthOptions.length - 1)]?.value;
        break;
      case 'oldest':
        targetMonth = monthOptions.oldestMonthValue;
        break;
      default:
        return;
    }
    
    if (targetMonth && targetMonth !== selectedMonth) {
      handleMonthChange({ target: { value: targetMonth } });
    }
  }, [monthOptions, selectedMonth, handleMonthChange]);

  /**
   * Handle drill-down requests from chart components
   */
  const handleDrilldown = useCallback((drilldownData) => {
    if (!drilldownData) {
      return;
    }
    
    setDrilldownModal({
      open: true,
      data: {
        ...drilldownData,
        // Ensure we pass current context
        stack: stackDimension,
        metric: metric,
        month: activeTab !== TABS.YTD ? selectedMonth : null
      }
    });
  }, [stackDimension, metric, activeTab, selectedMonth]);

  /**
   * Handle drill-down modal close
   */
  const handleDrilldownClose = useCallback(() => {
    setDrilldownModal({ open: false, data: null });
  }, []);

  /**
   * Handle CSV export requests from export button
   */
  const handleExport = useCallback(async (exportParams) => {
    const { exportType, chartType, selectedMetric, selectedStack, selectedMonth, selectedPeriod, dateRange, filename } = exportParams;
    
    console.log('QuickNotesReports: handleExport called with:', exportParams);
    
    try {
      // Build the export request data
      const exportData = {
        chart_type: chartType,
        metric: selectedMetric,
        stack: selectedStack,
        export_type: exportType
      };
      
      // Add period and date range parameters
      if (selectedPeriod) {
        exportData.period = selectedPeriod;
      }
      
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        exportData.start_date = dateRange.startDate;
        exportData.end_date = dateRange.endDate;
      }
      
      // Add month parameter for backward compatibility (for monthly charts that still use it)
      if (selectedMonth && chartType !== 'ytd') {
        exportData.month = selectedMonth;
      }
      
      // Determine the endpoint based on export type
      const endpoint = exportType === 'pdf' 
        ? '/admin/reports/quick_notes/export_aggregated_pdf'
        : '/admin/reports/quick_notes/export_aggregated';
      
      console.log('QuickNotesReports: Making request to:', endpoint, 'with data:', exportData);
      
      // Make POST request to export endpoint
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify(exportData)
      });
      
      console.log('QuickNotesReports: Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        // Try to get the error message from the response
        let errorMessage = `Export failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // If JSON parsing fails, try to get text
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (e2) {
            // Use default error message
          }
        }
        throw new Error(errorMessage);
      }
      
      // Handle response based on export type
      let blob;
      let downloadFilename = filename;
      
      if (exportType === 'pdf') {
        // Handle PDF response
        const pdfData = await response.arrayBuffer();
        blob = new Blob([pdfData], { type: 'application/pdf' });
        // Update filename extension if needed
        if (!downloadFilename.endsWith('.pdf')) {
          downloadFilename = downloadFilename.replace(/\.[^/.]+$/, '.pdf');
        }
      } else {
        // Handle CSV response
        const csvData = await response.text();
        blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      }
      
      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = downloadFilename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      // Show success message
      enqueueSnackbar(`Export "${filename}" completed successfully!`, { 
        variant: 'success',
        autoHideDuration: 4000
      });
      
    } catch (error) {
      console.error('QuickNotesReports: Export failed:', error);
      enqueueSnackbar(`Export failed: ${error.message}`, { 
        variant: 'error',
        autoHideDuration: 6000
      });
      throw error; // Re-throw so the export button can handle it
    }
  }, [enqueueSnackbar]);

  /**
   * Handle global refresh with retry logic
   */
  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
    setGlobalError(null);
    setDrilldownModal({ open: false, data: null });
    
    // Reset retry state on manual refresh
    setRetryState(prev => ({
      ...prev,
      retryCount: 0,
      consecutiveErrors: 0,
      isRetrying: false
    }));
    
    enqueueSnackbar('Refreshing reports data...', { variant: 'info' });
  }, [enqueueSnackbar]);

  /**
   * Smart retry logic with exponential backoff
   */
  const handleSmartRetry = useCallback(async (error, context = '', options = {}) => {
    const {
      forceRetry = false,
      customDelay = null,
      customMaxRetries = null
    } = options;

    const maxRetries = customMaxRetries || retryState.maxRetries;
    
    // Check if we should retry
    if (!forceRetry && retryState.retryCount >= maxRetries) {
      enqueueSnackbar(`Failed after ${maxRetries} attempts. Please check your connection and try again.`, { 
        variant: 'error',
        persist: true,
        action: (
          <Button color="inherit" size="small" onClick={() => handleSmartRetry(error, context, { forceRetry: true })}>
            Try Again
          </Button>
        )
      });
      return false;
    }

    // Check network status
    if (!isOnline) {
      enqueueSnackbar('You appear to be offline. Please check your connection.', { 
        variant: 'warning',
        persist: true
      });
      return false;
    }

    // Calculate retry delay with exponential backoff
    const baseDelay = customDelay || retryState.retryDelay;
    const retryDelay = baseDelay * Math.pow(2, retryState.retryCount);
    const jitteredDelay = retryDelay + (Math.random() * 1000); // Add jitter

    // Update retry state
    setRetryState(prev => ({
      ...prev,
      isRetrying: true,
      retryCount: prev.retryCount + 1,
      lastErrorTime: new Date().toISOString()
    }));

    // Show retry notification
    enqueueSnackbar(`Retrying ${context}... (${retryState.retryCount + 1}/${maxRetries})`, { 
      variant: 'info',
      autoHideDuration: Math.min(jitteredDelay, 5000)
    });

    try {
      // Wait for retry delay
      await new Promise(resolve => setTimeout(resolve, jitteredDelay));
      
      // Trigger refresh
      setRefreshTrigger(prev => prev + 1);
      
      // Reset consecutive errors on successful retry
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        consecutiveErrors: 0
      }));

      return true;
    } catch (retryError) {
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        consecutiveErrors: prev.consecutiveErrors + 1
      }));

      return false;
    }
  }, [retryState, isOnline, enqueueSnackbar]);

  /**
   * Determine if an error is retryable
   */
  const isRetryableError = useCallback((error) => {
    if (!error) return false;

    // Network errors are retryable
    if (error.networkError || error.message?.includes('Network')) {
      return true;
    }

    // GraphQL errors with specific codes are retryable
    if (error.graphQLErrors?.length > 0) {
      const retryableCodes = ['TIMEOUT', 'INTERNAL_ERROR', 'RATE_LIMITED'];
      return error.graphQLErrors.some(gqlError => 
        retryableCodes.includes(gqlError.extensions?.code)
      );
    }

    // HTTP status codes that are retryable
    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    if (error.status && retryableStatuses.includes(error.status)) {
      return true;
    }

    // Temporary failures
    if (error.message?.includes('timeout') || 
        error.message?.includes('rate limit') ||
        error.message?.includes('temporarily unavailable')) {
      return true;
    }

    return false;
  }, []);

  /**
   * Categorize errors for appropriate handling
   */
  const categorizeError = useCallback((error) => {
    if (!error) return 'unknown';

    // Network connectivity issues
    if (error.networkError || !isOnline) {
      return 'network';
    }

    // Authentication/authorization issues
    if (error.status === 401 || error.status === 403) {
      return 'auth';
    }

    // Server errors
    if (error.status >= 500) {
      return 'server';
    }

    // Rate limiting
    if (error.status === 429 || error.message?.includes('rate limit')) {
      return 'rate_limit';
    }

    // Client errors
    if (error.status >= 400 && error.status < 500) {
      return 'client';
    }

    // GraphQL specific errors
    if (error.graphQLErrors?.length > 0) {
      return 'graphql';
    }

    return 'unknown';
  }, [isOnline]);

  /**
   * Handle global errors from child components with smart retry
   */
  const handleGlobalError = useCallback((error, context = '') => {
    console.error(`QuickNotesReports: Global error ${context}:`, error);
    
    // Categorize error for appropriate handling
    const errorCategory = categorizeError(error);
    const isRetryable = isRetryableError(error);
    
    // Store error with context for debugging
    const errorWithContext = {
      ...error,
      context,
      category: errorCategory,
      isRetryable,
      timestamp: new Date().toISOString(),
      activeTab: TAB_LABELS[activeTab],
      metric,
      stackDimension,
      selectedMonth: activeTab !== TABS.YTD ? selectedMonth : null,
      retryState: { ...retryState }
    };
    
    setGlobalError(errorWithContext);
    
    // Update consecutive errors count
    setRetryState(prev => ({
      ...prev,
      consecutiveErrors: prev.consecutiveErrors + 1
    }));

    // Generate user-friendly error message based on category
    let userMessage;
    let variant = 'error';
    let actions = [];

    switch (errorCategory) {
      case 'network':
        userMessage = `Connection issue ${context}. ${isOnline ? 'Please check your network.' : 'You appear to be offline.'}`;
        variant = 'warning';
        break;
      case 'auth':
        userMessage = `Authorization error ${context}. Please refresh the page or contact support.`;
        actions.push(
          <Button key="refresh-page" color="inherit" size="small" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        );
        break;
      case 'server':
        userMessage = `Server error ${context}. Our team has been notified.`;
        break;
      case 'rate_limit':
        userMessage = `Too many requests ${context}. Please wait a moment before trying again.`;
        variant = 'warning';
        break;
      case 'client':
        userMessage = `Request error ${context}. Please check your inputs and try again.`;
        break;
      default:
        userMessage = `Error ${context}: ${error.message || 'An unexpected error occurred'}`;
    }

    // Add retry action for retryable errors
    if (isRetryable && retryState.retryCount < retryState.maxRetries) {
      actions.push(
        <Button 
          key="smart-retry" 
          color="inherit" 
          size="small" 
          onClick={() => handleSmartRetry(error, context)}
          disabled={retryState.isRetrying}
        >
          {retryState.isRetrying ? 'Retrying...' : `Retry (${retryState.retryCount}/${retryState.maxRetries})`}
        </Button>
      );
    }

    // Always add manual refresh option
    actions.push(
      <Button key="manual-refresh" color="inherit" size="small" onClick={handleRefresh}>
        Refresh
      </Button>
    );

    // Show notification with appropriate actions
    enqueueSnackbar(userMessage, { 
      variant,
      autoHideDuration: errorCategory === 'rate_limit' ? 10000 : 8000,
      persist: errorCategory === 'auth' || errorCategory === 'network',
      action: actions.length > 0 ? (
        <div style={{ display: 'flex', gap: 8 }}>
          {actions}
        </div>
      ) : undefined
    });

    // Auto-retry for certain error types if not already retrying
    if (isRetryable && !retryState.isRetrying && errorCategory === 'network' && retryState.retryCount === 0) {
      setTimeout(() => {
        handleSmartRetry(error, context);
      }, 2000); // Wait 2 seconds before auto-retry
    }
  }, [
    enqueueSnackbar, 
    activeTab, 
    metric, 
    stackDimension, 
    selectedMonth, 
    handleRefresh,
    categorizeError,
    isRetryableError,
    handleSmartRetry,
    retryState,
    isOnline
  ]);

  // =============================================================================
  // DATA FLOW MONITORING AND EFFECTS
  // =============================================================================

  /**
   * Log component mount and user context
   */
  useEffect(() => {
    if (currentUser) {
    }
  }, [currentUser, userCompany]);

  /**
   * Monitor data flow and state changes for debugging
   */
  useEffect(() => {
    const stateSnapshot = {
      activeTab: TAB_LABELS[activeTab],
      metric: METRIC_LABELS[metric],
      stackDimension: STACK_LABELS[stackDimension],
      selectedMonth: activeTab !== TABS.YTD ? selectedMonth : 'N/A (YTD)',
      drilldownModalOpen: drilldownModal.open,
      hasGlobalError: !!globalError,
      refreshTrigger
    };
    
    // Track state changes for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'reports_state_change', {
        event_category: 'quick_notes_reports',
        event_label: stateSnapshot.activeTab,
        custom_parameters: {
          metric: stateSnapshot.metric,
          stack: stateSnapshot.stackDimension
        }
      });
    }
  }, [activeTab, metric, stackDimension, selectedMonth, drilldownModal.open, globalError, refreshTrigger]);

  /**
   * Monitor chart component prop changes and data flow
   */
  const chartPropsMonitor = useMemo(() => {
    const commonProps = {
      metric,
      stack: stackDimension,
      onDrilldown: 'function',
      onError: 'function',
      refreshTrigger
    };

    const specificProps = {
      [TABS.YTD]: {},
      [TABS.OWNER]: { month: selectedMonth }
    };

    const fullProps = {
      ...commonProps,
      ...specificProps[activeTab]
    };
    
    return fullProps;
  }, [metric, stackDimension, selectedMonth, activeTab, refreshTrigger]);

  /**
   * Auto-validate and correct selected month
   */
  useEffect(() => {
    if (monthOptions.length === 0) return; // Wait for options to load
    
    // Check if current selectedMonth is valid
    if (!validateMonthSelection(selectedMonth)) {
      
      // Auto-correct to current month
      const currentMonthValue = monthOptions.currentMonthValue;
      if (currentMonthValue) {
        setSelectedMonth(currentMonthValue);
        setMonthValidationError(null);
        
        enqueueSnackbar('Month automatically corrected to current month', { 
          variant: 'info',
          autoHideDuration: 3000
        });
      }
    } else {
      // Clear any existing validation errors if month is now valid
      setMonthValidationError(null);
    }
  }, [monthOptions, selectedMonth, validateMonthSelection, enqueueSnackbar]);

  /**
   * Note: Timezone handling removed due to Company.timeZone field not being available in GraphQL schema
   * Using system timezone for month calculations
   */

  /**
   * Keyboard shortcuts for month navigation
   */
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only handle shortcuts when month tabs are active and modal is not open
      if (activeTab !== TABS.OWNER || drilldownModal.open) {
        return;
      }

      // Check for modifier keys to avoid conflicts
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          handleQuickMonthSelection('previous');
          break;
        case 'ArrowRight':
          event.preventDefault();
          // Navigate to next month (more recent)
          const currentIndex = monthOptions.findIndex(opt => opt.value === selectedMonth);
          const nextMonth = monthOptions[Math.max(currentIndex - 1, 0)]?.value;
          if (nextMonth && nextMonth !== selectedMonth) {
            handleMonthChange({ target: { value: nextMonth } });
          }
          break;
        case 'Home':
          event.preventDefault();
          handleQuickMonthSelection('current');
          break;
        case 'End':
          event.preventDefault();
          handleQuickMonthSelection('oldest');
          break;
      }
    };

    // Only add listener if we're on a monthly view
    if (activeTab === TABS.OWNER) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [activeTab, drilldownModal.open, monthOptions, selectedMonth, handleQuickMonthSelection, handleMonthChange]);

  /**
   * Network status monitoring
   */
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkRecovery(true);
      
      // If we had network errors, offer to retry
      if (globalError?.category === 'network') {
        enqueueSnackbar('Connection restored! Click to retry loading data.', {
          variant: 'success',
          action: (
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry Now
            </Button>
          ),
          autoHideDuration: 5000
        });
      }
      
      // Auto-clear network recovery flag after a delay
      setTimeout(() => setNetworkRecovery(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setNetworkRecovery(false);
      
      enqueueSnackbar('You appear to be offline. Some features may not work.', {
        variant: 'warning',
        persist: true
      });
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [globalError, enqueueSnackbar, handleRefresh]);

  /**
   * Automatic error recovery monitoring
   */
  useEffect(() => {
    // If we have consecutive errors, implement circuit breaker pattern
    if (retryState.consecutiveErrors >= 5) {    
      enqueueSnackbar('Multiple errors detected. Please wait a moment before trying again.', {
        variant: 'error',
        persist: true,
        action: (
          <Button 
            color="inherit" 
            size="small" 
            onClick={() => {
              setRetryState(prev => ({ ...prev, consecutiveErrors: 0, retryCount: 0 }));
              handleRefresh();
            }}
          >
            Reset & Retry
          </Button>
        )
      });
    }
  }, [retryState.consecutiveErrors, enqueueSnackbar, handleRefresh]);

  /**
   * Global retry state monitoring
   */
  useEffect(() => { 
    // Log retry patterns for debugging
    if (retryState.isRetrying) {
      console.log(`QuickNotesReports: Currently retrying (attempt ${retryState.retryCount}/${retryState.maxRetries})`);
    }
    
    if (retryState.consecutiveErrors > 0) {
      console.warn(`QuickNotesReports: ${retryState.consecutiveErrors} consecutive errors detected`);
    }
  }, [retryState]);

  // =============================================================================
  // LOADING AND ERROR STATES
  // =============================================================================

  // Show loading if essential data is loading
  if (userLoading || categoriesLoading || globalLoading) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={48} />
          <Typography variant="h6" color="textSecondary">
            Loading Quick Notes Reports...
          </Typography>
        </div>
      </Container>
    );
  }

  // Show error if essential data failed to load
  if (userError || categoriesError || globalError) {
    const error = userError || categoriesError || globalError;
    return (
      <Container maxWidth="lg" className={classes.root}>
        <div className={classes.errorContainer}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Retry
              </Button>
            }
          >
            <Typography variant="h6">Failed to Load Reports</Typography>
            <Typography variant="body2">
              {error.message || 'An unexpected error occurred while loading the reports.'}
            </Typography>
          </Alert>
        </div>
      </Container>
    );
  }

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  /**
   * Render the controls row with metric/stack selectors and actions
   */
  const renderControlsRow = () => (
    <div className={classes.controlsRow}>
      <div className={classes.metricsControls}>
        {/* Metric Selector */}
        <FormControl variant="outlined" size="small">
          <InputLabel>Metric</InputLabel>
          <Select
            value={metric}
            onChange={handleMetricChange}
            label="Metric"
          >
            {Object.entries(METRIC_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Stack Dimension Selector */}
        <FormControl variant="outlined" size="small">
          <InputLabel>Group By</InputLabel>
          <Select
            value={stackDimension}
            onChange={handleStackDimensionChange}
            label="Group By"
          >
            {Object.entries(STACK_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Period Selector for both tabs */}
        <FormControl variant="outlined" size="small">
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            label="Period"
          >
            {Object.entries(PERIOD_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Date Range Picker */}
        {selectedPeriod === PERIODS.CUSTOM && (
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              type="date"
              label="Start Date"
              variant="outlined"
              size="small"
              value={customDateRange.startDate || ''}
              onChange={(e) => handleCustomDateRangeChange('startDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: customDateRange.endDate || undefined
              }}
            />
            <TextField
              type="date"
              label="End Date"
              variant="outlined"
              size="small"
              value={customDateRange.endDate || ''}
              onChange={(e) => handleCustomDateRangeChange('endDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: customDateRange.startDate || undefined
              }}
            />
          </Box>
        )}
      </div>

      <div className={classes.actionControls}>
        {/* Export Button */}
        <QuickNotesExportButton
          data={[]} // Will be populated by the export handler from server data
          chartType={activeTab === TABS.YTD ? 'ytd' : 'owner'}
          selectedMetric={metric}
          selectedStack={stackDimension}
          selectedMonth={activeTab !== TABS.YTD ? selectedMonth : null}
          selectedPeriod={selectedPeriod}
          dateRange={getActiveDateRange()}
          onExport={handleExport}
          onError={handleGlobalError}
          enqueueSnackbar={enqueueSnackbar}
        />

        {/* Network Status Indicator */}
        {!isOnline && (
          <Chip
            label="Offline"
            size="small"
            color="default"
            style={{ 
              backgroundColor: '#ff9800', 
              color: 'white',
              marginRight: 8 
            }}
          />
        )}
        
        {networkRecovery && (
          <Chip
            label="Connection Restored"
            size="small"
            color="primary"
            style={{ marginRight: 8 }}
          />
        )}

        {/* Retry Status Indicator */}
        {retryState.isRetrying && (
          <Chip
            label={`Retrying... (${retryState.retryCount}/${retryState.maxRetries})`}
            size="small"
            color="secondary"
            style={{ marginRight: 8 }}
          />
        )}

      </div>
    </div>
  );

  /**
   * Render the appropriate chart based on active tab with enhanced data flow
   */
  const renderActiveChart = () => {
    // Get current date range
    const dateRange = getActiveDateRange();
    
    // Common props shared by all charts
    const commonProps = {
      metric,
      stack: stackDimension,
      onDrilldown: handleDrilldown,
      onError: handleGlobalError,
      refreshTrigger,
      enqueueSnackbar,
      dateRange,
      selectedPeriod
    };

    // Enhanced chart wrapper with error handling
    const withEnhancedWrapper = (ChartComponent, chartProps, chartName) => {
      // Create a wrapper component that catches errors
      const WrappedChart = () => {
        try {
          return <ChartComponent {...chartProps} />;
        } catch (error) {
          console.error(`QuickNotesReports: ${chartName} render error`, error);
          handleGlobalError(error, `rendering ${chartName}`);
          
          return (
            <Card style={{ padding: 16, textAlign: 'center' }}>
              <Alert severity="error" style={{ marginBottom: 16 }}>
                <Typography variant="h6">Chart Error</Typography>
                <Typography variant="body2">
                  Failed to render {chartName}: {error.message}
                </Typography>
              </Alert>
              <Button 
                variant="outlined" 
                onClick={() => setRefreshTrigger(prev => prev + 1)}
              >
                Retry Chart
              </Button>
            </Card>
          );
        }
      };
      
      return (
        <ErrorBoundary>
          <WrappedChart />
        </ErrorBoundary>
      );
    };

    // Validate required props before rendering
    const validateChartProps = (requiredProps) => {
      const missing = requiredProps.filter(prop => 
        commonProps[prop] === undefined || commonProps[prop] === null
      );
      
      if (missing.length > 0) {
        console.warn(`QuickNotesReports: Missing required props for chart: ${missing.join(', ')}`);
        return false;
      }
      return true;
    };

    switch (activeTab) {
      case TABS.YTD:
        if (!validateChartProps(['metric', 'stack', 'onDrilldown', 'onError'])) {
          return (
            <Alert severity="warning">
              Invalid configuration for By Type chart. Please refresh the page.
            </Alert>
          );
        }
        
        return withEnhancedWrapper(
          QuickNotesYtdChart,
          {
            ...commonProps,
            key: `ytd-${metric}-${stackDimension}-${selectedPeriod}-${refreshTrigger}`
          },
          'By Type Chart'
        );
      
      case TABS.OWNER:
        if (!validateChartProps(['metric', 'stack', 'onDrilldown', 'onError'])) {
          return (
            <Alert severity="warning">
              Invalid configuration for Owner chart. Please refresh the page.
            </Alert>
          );
        }
        
        return withEnhancedWrapper(
          QuickNotesOwnerChart,
          {
            ...commonProps,
            key: `owner-${metric}-${stackDimension}-${selectedPeriod}-${refreshTrigger}`
          },
          'Owner Chart'
        );
      
      default:
        console.error(`QuickNotesReports: Unknown tab ${activeTab}`);
        return (
          <Alert severity="warning">
            <Typography variant="h6">Unknown View</Typography>
            <Typography variant="body2">
              Tab "{activeTab}" is not recognized. Please refresh the page or contact support.
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setActiveTab(TABS.YTD)} 
              style={{ marginTop: 8 }}
            >
              Go to By Type View
            </Button>
          </Alert>
        );
    }
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Container maxWidth="lg" className={classes.root}>
      {/* Header */}
      <Card className={classes.header} elevation={1}>
        <div className={classes.headerTitle}>
          <AssessmentIcon />
          <Typography variant="h4" component="h1">
            Quick Notes Reports
          </Typography>
        </div>
        <Typography variant="body1" color="textSecondary">
          Analyze Quick Notes activity with detailed breakdowns and drill-down capabilities.
          {userCompany?.name && ` Data for ${userCompany.name}.`}
        </Typography>
      </Card>

      {/* Tabs */}
      <Paper className={classes.tabsContainer} elevation={1}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="Quick Notes report views"
        >
          {Object.entries(TAB_LABELS).map(([value, label]) => (
            <Tab
              key={value}
              label={label}
              id={`reports-tab-${value}`}
              aria-controls={`reports-tabpanel-${value}`}
            />
          ))}
        </Tabs>

        {/* Tab Panel Content */}
        <div
          className={classes.tabPanel}
          role="tabpanel"
          id={`reports-tabpanel-${activeTab}`}
          aria-labelledby={`reports-tab-${activeTab}`}
        >
          {/* Controls Row */}
          {renderControlsRow()}

          {/* Chart Container */}
          <div className={classes.chartContainer}>
            {renderActiveChart()}
          </div>
        </div>
      </Paper>

      {/* Drill-down Modal */}
      <QuickNotesDrilldownModal
        open={drilldownModal.open}
        drilldownData={drilldownModal.data}
        onClose={handleDrilldownClose}
        onError={handleGlobalError}
        enqueueSnackbar={enqueueSnackbar}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      />
    </Container>
  );
};

QuickNotesReports.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(QuickNotesReports);
